import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { CalendarCheck, Check, X, Minus, Circle, Pencil, ChevronDown } from 'lucide-react'

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
const SLOTS = ['morning', 'forenoon', 'afternoon', 'night']

const StatusIcon = ({ status }) => {
  switch (status) {
    case 'done':
      return <Check size={16} className="text-green-400" strokeWidth={3} />
    case 'missed':
      return <X size={16} className="text-gray-500" strokeWidth={3} />
    case 'today':
      return <Circle size={12} className="text-orange-500 fill-orange-500" />
    default:
      return <Minus size={16} className="text-gray-600" />
  }
}

const WeeklyPlanCard = () => {
  const { t } = useTranslation()
  const [data, setData] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [showLastWeek, setShowLastWeek] = useState(false)
  const [draftSlots, setDraftSlots] = useState({})
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)

  const fetchData = async () => {
    try {
      const res = await fetch('/api/v1/weekly_plan')
      if (res.ok) setData(await res.json())
    } catch (_) { /* 取得失敗時はカード自体を表示しない */ }
  }

  useEffect(() => { fetchData() }, [])

  if (!data) return null

  const { planning_window, target_is_current_week, target_slots, current_review, last_week_review } = data

  const openModal = () => {
    setDraftSlots(target_slots || {})
    setSaveError(null)
    setIsModalOpen(true)
  }

  const toggleSlot = (day, slot) => {
    setDraftSlots(prev => {
      const next = { ...prev }
      if (next[day] === slot) {
        delete next[day]
      } else {
        next[day] = slot
      }
      return next
    })
  }

  const handleSave = async () => {
    if (Object.keys(draftSlots).length === 0) {
      setSaveError(t('weekly_plan.no_days'))
      return
    }
    setSaving(true)
    setSaveError(null)
    try {
      const res = await fetch('/api/v1/weekly_plan', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        },
        body: JSON.stringify({ slots: draftSlots })
      })
      if (!res.ok) throw new Error('save failed')
      await fetchData()
      setIsModalOpen(false)
    } catch (_) {
      setSaveError(t('weekly_plan.save_error'))
    } finally {
      setSaving(false)
    }
  }

  const renderReviewDays = (review) => (
    <div className="flex flex-wrap gap-2 mt-3">
      {review.days.map(({ day, slot, status }) => (
        <div
          key={day}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-bold ${
            status === 'done'
              ? 'bg-green-500/10 border-green-500/30 text-green-400'
              : status === 'today'
                ? 'bg-orange-500/10 border-orange-500/40 text-orange-400'
                : 'bg-[#2c2c2e] border-gray-700 text-gray-400'
          }`}
        >
          <span>{t(`weekly_plan.day_${day}`)}</span>
          <span className="font-normal text-[10px] opacity-80">{t(`weekly_plan.slot_${slot}`)}</span>
          <StatusIcon status={status} />
        </div>
      ))}
    </div>
  )

  const bannerTitleKey = target_is_current_week ? 'weekly_plan.banner_title_this' : 'weekly_plan.banner_title_next'

  const lastWeekPerfect = last_week_review && last_week_review.achieved === last_week_review.total

  const renderReviewMessage = (review) => (
    <p className={`text-sm mt-2 font-bold ${review.achieved === review.total ? 'text-green-400' : 'text-gray-300'}`}>
      {review.achieved === review.total
        ? t('weekly_plan.perfect')
        : t('weekly_plan.achieved', { achieved: review.achieved, total: review.total })}
    </p>
  )

  // 先週の答え合わせ（今週カードの下段に折りたたみで格納。畳んだ状態でも達成数は見える）
  const renderLastWeekToggle = () => (
    <div className="mt-3 pt-3 border-t border-gray-800">
      <button
        onClick={() => setShowLastWeek(prev => !prev)}
        className="flex items-center gap-2 w-full text-left"
        type="button"
      >
        <CalendarCheck size={14} className="text-gray-500 shrink-0" />
        <span className="text-gray-400 text-xs font-bold">{t('weekly_plan.review_title')}</span>
        <span className={`text-xs font-bold ${lastWeekPerfect ? 'text-green-400' : 'text-gray-300'}`}>
          {last_week_review.achieved}/{last_week_review.total}
        </span>
        <ChevronDown
          size={16}
          className={`text-gray-500 ml-auto shrink-0 transition-transform duration-300 ${showLastWeek ? 'rotate-180' : ''}`}
        />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-out ${showLastWeek ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
        {renderReviewMessage(last_week_review)}
        {renderReviewDays(last_week_review)}
      </div>
    </div>
  )

  return (
    <>
      {/* 今週の計画がない週向けフォールバック（答え合わせの置き場所がないため単独カード） */}
      {last_week_review && !current_review && (
        <div className="bg-[#1c1c1e] border border-gray-800 rounded-xl p-4 mb-4">
          <div className="flex items-center gap-2">
            <CalendarCheck size={18} className="text-orange-500" />
            <p className="text-white font-bold text-sm">{t('weekly_plan.review_title')}</p>
          </div>
          {renderReviewMessage(last_week_review)}
          {renderReviewDays(last_week_review)}
        </div>
      )}

      {/* 今週の計画（計画があれば常時表示） */}
      {current_review && (
        <div className="bg-[#1c1c1e] border border-gray-800 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CalendarCheck size={18} className="text-orange-500" />
              <p className="text-white font-bold text-sm">{t('weekly_plan.current_title')}</p>
              {!current_review.finished && (
                <span className="text-[10px] text-gray-500 border border-gray-700 rounded px-1.5 py-0.5">
                  {t('weekly_plan.in_progress')}
                </span>
              )}
            </div>
            {planning_window && target_is_current_week && (
              <button
                onClick={openModal}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-white transition-colors"
                type="button"
              >
                <Pencil size={12} />
                {t('weekly_plan.edit_cta')}
              </button>
            )}
          </div>
          {renderReviewDays(current_review)}
          {last_week_review && renderLastWeekToggle()}
        </div>
      )}

      {/* 来週の計画（土日に作成済みの場合のみ） */}
      {planning_window && !target_is_current_week && target_slots && (
        <div className="bg-[#1c1c1e] border border-gray-800 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CalendarCheck size={18} className="text-orange-500" />
              <p className="text-white font-bold text-sm">{t('weekly_plan.next_title')}</p>
            </div>
            <button
              onClick={openModal}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-white transition-colors"
              type="button"
            >
              <Pencil size={12} />
              {t('weekly_plan.edit_cta')}
            </button>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {DAYS.filter(day => target_slots[day]).map(day => (
              <div key={day} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-orange-500/30 bg-orange-500/10 text-orange-400 text-xs font-bold">
                <span>{t(`weekly_plan.day_${day}`)}</span>
                <span className="font-normal text-[10px] opacity-80">{t(`weekly_plan.slot_${target_slots[day]}`)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 火〜金・今週計画なし: 機能の存在を思い出させる控えめな1行 */}
      {!planning_window && !current_review && (
        <p className="flex items-center gap-1.5 text-gray-500 text-xs mb-4">
          <CalendarCheck size={13} className="shrink-0" />
          {t('weekly_plan.no_plan_hint')}
        </p>
      )}

      {/* 計画作成バナー（土〜月・未作成のときのみ） */}
      {planning_window && !target_slots && (
        <div className="bg-blue-500/10 border border-blue-500/50 rounded-xl p-4 mb-4">
          <div className="flex items-center gap-3">
            <CalendarCheck className="text-blue-400 flex-shrink-0" size={20} />
            <p className="text-blue-400 font-bold text-sm flex-1">{t(bannerTitleKey)}</p>
            <button
              onClick={openModal}
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold text-xs px-4 py-2 rounded-lg hover:opacity-90 transition-opacity flex-shrink-0"
              type="button"
            >
              {t('weekly_plan.plan_cta')}
            </button>
          </div>
          <p className="text-gray-400 text-xs mt-2">{t('weekly_plan.banner_desc')}</p>
        </div>
      )}

      {/* 計画グリッドモーダル */}
      {isModalOpen && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setIsModalOpen(false)}></div>
          <div className="relative w-full max-w-md bg-[#1c1c1e] rounded-2xl border border-gray-800 p-5 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-white font-bold text-base flex items-center gap-2">
                <CalendarCheck size={18} className="text-orange-500" />
                {t(bannerTitleKey)}
              </h2>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-500 hover:text-gray-300 transition-colors" type="button">
                <X size={20} />
              </button>
            </div>
            <p className="text-gray-400 text-xs mb-4">{t('weekly_plan.modal_desc')}</p>

            <div className="space-y-2">
              {DAYS.map(day => (
                <div key={day} className="flex items-center gap-2">
                  <span className={`w-8 text-center text-sm font-bold shrink-0 ${
                    day === 'sat' ? 'text-blue-400' : day === 'sun' ? 'text-red-400' : 'text-gray-300'
                  }`}>
                    {t(`weekly_plan.day_${day}`)}
                  </span>
                  <div className="flex-1 grid grid-cols-4 gap-1.5">
                    {SLOTS.map(slot => (
                      <button
                        key={slot}
                        onClick={() => toggleSlot(day, slot)}
                        className={`py-2 rounded-lg text-xs font-bold border transition-colors ${
                          draftSlots[day] === slot
                            ? 'bg-gradient-to-r from-orange-500 to-orange-600 border-orange-500 text-white'
                            : 'bg-[#2c2c2e] border-gray-700 text-gray-400 hover:border-gray-500'
                        }`}
                        type="button"
                      >
                        {t(`weekly_plan.slot_${slot}`)}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <p className="text-gray-500 text-[11px] mt-3">{t('weekly_plan.modal_hint')}</p>
            {saveError && <p className="text-red-400 text-xs mt-2">{saveError}</p>}

            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full mt-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold py-3 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
              type="button"
            >
              {saving ? t('weekly_plan.saving') : t('weekly_plan.save')}
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

export default WeeklyPlanCard
